import { normalizeMessage } from '../../conversations/services/conversationService';
import { Message } from '../../conversations/types/Conversation';
import { WebWidgetTypingIndicator } from '../types/webWidget';
import { normalizeTypingIndicators } from '../utils/webWidgetTypingIndicators';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const WEB_WIDGET_BASE_PATH = '/api/v1/web-widget';
const WEB_WIDGET_TENANT_HEADER = 'X-Web-Widget-Tenant-Code';
const WEB_WIDGET_KEY_HEADER = 'X-Web-Widget-Key';

export const WEB_WIDGET_SESSION_EVENT_TYPES = {
  connected: 'connected',
  heartbeat: 'heartbeat',
  typingUpdated: 'typing.updated',
  messageCreated: 'message.created',
} as const;

export type WebWidgetRealtimeStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

export interface WebWidgetSessionEventEnvelope {
  type: string;
  timestampUtc?: string;
  data?: unknown;
}

export interface ParsedSseEvent {
  event?: string;
  data: string;
}

type FetchLike = typeof fetch;

export interface WebWidgetSessionEventsClientConfig {
  tenantCode: string;
  widgetKey: string;
  baseUrl?: string;
  origin?: string;
  getSessionToken?: () => string | null;
  fetch?: FetchLike;
}

export interface WebWidgetSessionEventsHandlers {
  onStatusChange?: (status: WebWidgetRealtimeStatus) => void;
  onTypingUpdated?: (indicators: WebWidgetTypingIndicator[]) => void;
  onMessageCreated?: (message: Message) => void;
}

export interface WebWidgetSessionEventsConnection {
  close: () => void;
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

const ensureFetch = (fetchImpl?: FetchLike): FetchLike => {
  if (fetchImpl) {
    return fetchImpl;
  }

  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }

  throw new Error('Fetch API is not available in this environment.');
};

export const parseSseChunk = (buffer: string): { events: ParsedSseEvent[]; remainder: string } => {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const remainder = blocks.pop() ?? '';
  const events: ParsedSseEvent[] = [];

  blocks.forEach(block => {
    const lines = block.split('\n').filter(line => line.length > 0);
    if (lines.length === 0) {
      return;
    }

    let eventName: string | undefined;
    const dataLines: string[] = [];

    lines.forEach(line => {
      if (line.startsWith(':')) {
        return;
      }

      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
        return;
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trimStart());
      }
    });

    if (dataLines.length === 0) {
      return;
    }

    events.push({
      event: eventName,
      data: dataLines.join('\n'),
    });
  });

  return { events, remainder };
};

export const parseSessionEventEnvelope = (rawData: string): WebWidgetSessionEventEnvelope | null => {
  try {
    const parsed = JSON.parse(rawData) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const envelope = parsed as Record<string, unknown>;
    const type = typeof envelope.type === 'string' ? envelope.type.trim() : '';
    if (!type) {
      return null;
    }

    return {
      type,
      timestampUtc: typeof envelope.timestampUtc === 'string' ? envelope.timestampUtc : undefined,
      data: envelope.data,
    };
  } catch {
    return null;
  }
};

const extractTypingIndicators = (data: unknown): WebWidgetTypingIndicator[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const payload = data as Record<string, unknown>;
  return normalizeTypingIndicators(payload.typingIndicators);
};

const extractMessage = (data: unknown): Message | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const payload = data as Record<string, unknown>;
  if (!payload.message) {
    return null;
  }

  try {
    return normalizeMessage(payload.message);
  } catch {
    return null;
  }
};

export const handleWebWidgetSessionEvent = (
  envelope: WebWidgetSessionEventEnvelope,
  handlers: WebWidgetSessionEventsHandlers,
): void => {
  switch (envelope.type) {
    case WEB_WIDGET_SESSION_EVENT_TYPES.connected:
    case WEB_WIDGET_SESSION_EVENT_TYPES.heartbeat:
      handlers.onStatusChange?.('connected');
      return;
    case WEB_WIDGET_SESSION_EVENT_TYPES.typingUpdated:
      handlers.onTypingUpdated?.(extractTypingIndicators(envelope.data));
      return;
    case WEB_WIDGET_SESSION_EVENT_TYPES.messageCreated: {
      const message = extractMessage(envelope.data);
      if (message) {
        handlers.onMessageCreated?.(message);
      }
      return;
    }
    default:
      return;
  }
};

export const connectWebWidgetSessionEvents = (
  config: WebWidgetSessionEventsClientConfig,
  handlers: WebWidgetSessionEventsHandlers,
): WebWidgetSessionEventsConnection => {
  const baseUrl = (config.baseUrl ?? API_BASE_URL).replace(/\/$/, '');
  const fetchImpl = ensureFetch(config.fetch);
  const tenantCode = config.tenantCode.trim();
  const widgetKey = config.widgetKey.trim();
  const abortController = new AbortController();
  let reconnectAttempt = 0;
  let closed = false;

  const buildHeaders = (): Headers => {
    const headers = new Headers();
    headers.set('Accept', 'text/event-stream');

    if (tenantCode) {
      headers.set(WEB_WIDGET_TENANT_HEADER, tenantCode);
    }

    if (widgetKey) {
      headers.set(WEB_WIDGET_KEY_HEADER, widgetKey);
    }

    const sessionToken = config.getSessionToken?.()?.trim();
    if (sessionToken) {
      headers.set('Authorization', `Bearer ${sessionToken}`);
    }

    if (config.origin) {
      headers.set('Origin', config.origin);
    }

    return headers;
  };

  const scheduleReconnect = () => {
    if (closed) {
      return;
    }

    handlers.onStatusChange?.('disconnected');
    const delayMs = Math.min(RECONNECT_BASE_MS * (2 ** reconnectAttempt), RECONNECT_MAX_MS);
    reconnectAttempt += 1;

    window.setTimeout(() => {
      if (!closed) {
        void connect();
      }
    }, delayMs);
  };

  const connect = async () => {
    if (closed) {
      return;
    }

    handlers.onStatusChange?.('connecting');

    try {
      const response = await fetchImpl(`${baseUrl}${WEB_WIDGET_BASE_PATH}/session/events`, {
        method: 'GET',
        credentials: 'include',
        headers: buildHeaders(),
        signal: abortController.signal,
      });

      if (!response.ok || !response.body) {
        scheduleReconnect();
        return;
      }

      reconnectAttempt = 0;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!closed) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const { events, remainder } = parseSseChunk(buffer);
        buffer = remainder;

        events.forEach(event => {
          const envelope = parseSessionEventEnvelope(event.data);
          if (envelope) {
            handleWebWidgetSessionEvent(envelope, handlers);
          }
        });
      }

      if (!closed) {
        scheduleReconnect();
      }
    } catch (error) {
      if (closed || (error instanceof DOMException && error.name === 'AbortError')) {
        return;
      }

      scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      closed = true;
      abortController.abort();
      handlers.onStatusChange?.('idle');
    },
  };
};
